import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, CacheKeyEnum, ConfirmationComponent, LeadSource, LeadSourceService, Pager } from 'lib-trend-core';
import { debounceTime, distinctUntilChanged, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { ModalLeadSourceComponent } from './modal-lead-source/modal-lead-source.component';

@Component({
    selector: 'app-lead-source',
    // templateUrl: './lead-source.component.html',
    templateUrl: './lead-source.v2.component.html',
    styleUrls: ['./lead-source.component.scss', './lead-source.v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LeadSourceComponent extends AbstractComponent implements OnInit {
  pager: Pager<LeadSource> = new Pager<LeadSource>({ perPage: 10 });
  listObservable: Observable<Pager<LeadSource>>;

  private searchTerm: Subject<string> = new Subject<string>();

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    private leadSourceService: LeadSourceService,
  ) {
    super(injector);
    this.setupForm();
    this.loadingContent = true;
    this.cacheKey = CacheKeyEnum.LEADS_SOURCES_LIST;
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    const cached = this.restoreState<LeadSource>();
    if (!cached) {
      this.getList();
      return;
    }
    this.pager = cached.pager;
    this.searchString = cached.search ?? '';
    this.loadingContent = false;

    if (this.searchString) {
      this.formGroup.get('search').setValue(this.searchString);
    }

    this.setupObservableSearch();
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      search: [''],
    });
  }

  setupObservableSearch() {
    this.listObservable = this.searchTerm
      .pipe(takeUntil(this.destroy$))
      .pipe(debounceTime(500))
      .pipe(distinctUntilChanged())
      .pipe(switchMap(term => {
        return this.leadSourceService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams);
      }));
    this.listObservable.subscribe((pager: Pager<LeadSource>) => {
      this.pager = pager;
      this.persistListState(this.pager, this.searchString, this.searchParams);
    });
  }

  getList() {
    this.loadingContent = true;

    this.searchParams = {
      company: this.getIDCurrentCompany(),
    };

    this.leadSourceService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pager: Pager<LeadSource>) => {
          this.pager = pager;
          this.setupObservableSearch();
          this.persistListState(this.pager, this.searchString, this.searchParams);
        },
        complete: () => this.loadingContent = false,
      });
  }

  search(termo: string) {
    this.pager.page = 1;
    this.searchString = termo;
    this.searchTerm.next(termo);
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getList();
  }

  add(): void {
    this.dialog.open(ModalLeadSourceComponent, {
      width: '600px',
    })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: LeadSource) => {
        if(result) this.getList();
      });
  }

  edit(leadSource: LeadSource): void {

    this.dialog.open(ModalLeadSourceComponent, {
      width: '600px',
      data: leadSource
    })
      .afterClosed().subscribe(result => {
       if(result) this.getList();
      });
  }

  delete(leadSource: LeadSource): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
    });

    dialogRef.afterClosed()
      .subscribe((result: boolean) => {
        if (Boolean(result) === true) {
          this.leadSourceService.delete(leadSource._id)
            .pipe(takeUntil(this.destroy$)).subscribe({
              next: () => {
                this.alertService.success(this.t('leadSource.list.deleteSuccess'));
                this.getList();
              },
              error: (error) => { this.alertService.error(error.error.message) }
            });
        }
      });
  }

}
